import { useAuth0 } from '@auth0/auth0-react';
import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { EventEmitter } from 'fbemitter';

import {
  createConnection,
  deleteConnection,
  disableConnection,
  freezeLearning,
  getAutoapprovalMode,
  getConnectionsForConversation,
  getConnector,
  getConnectors,
  getLearning,
  getPublicKey,
  getStaticIp,
  getUserConnections,
  listConnectionData,
  setAutoapprovalMode,
  streamingEnableConnection,
  updateConnection,
  updateConnectionCustomInstructions,
  updateConnectionName,
  updateLearning
} from './api';
import {
  ConnectionData,
  DataConnection,
  DataConnector,
  ListConnectionDataOptions,
  ListConnectionDataParams,
  PublicKey,
  StaticIpResponse
} from './types';

// Create a global event emitter instance for data connection events
export const dataConnectionEventEmitter = new EventEmitter();

export const usePublicKeys = () => {
  const { getAccessTokenSilently } = useAuth0();

  return useQuery<PublicKey, Error>({
    queryKey: ['data_connectors/public_keys'],
    queryFn: async (): Promise<PublicKey> => {
      const accessToken = await getAccessTokenSilently();
      return getPublicKey({ accessToken });
    },
    staleTime: Infinity,
    refetchOnWindowFocus: false
  });
};

export const getUserConnectionsQueryKey = () => ['data_connectors/user_connections'];

export const getConversationConnectionsQueryKey = (conversationId: string) => [
  'data_connectors/conversation_connections',
  conversationId
];

export const useConversationConnections = ({
  conversationId
}: {
  conversationId: string | null;
}) => {
  const { getAccessTokenSilently } = useAuth0();

  return useQuery<DataConnection[], Error>({
    queryKey: getConversationConnectionsQueryKey(conversationId ?? ''),
    queryFn: async () => {
      if (!conversationId) throw new Error('Conversation id is required');
      const accessToken = await getAccessTokenSilently();
      return getConnectionsForConversation({ accessToken, conversationId });
    },
    enabled: !!conversationId,
    staleTime: Infinity,
    refetchOnWindowFocus: false
  });
};

export const useUserConnections = () => {
  const { getAccessTokenSilently } = useAuth0();

  return useQuery<DataConnection[], Error>({
    queryKey: ['data_connectors/user_connections'],
    queryFn: async () => {
      const accessToken = await getAccessTokenSilently();
      return getUserConnections({ accessToken });
    },
    staleTime: Infinity,
    refetchOnWindowFocus: false
  });
};

export const useConnectors = () => {
  const { getAccessTokenSilently } = useAuth0();

  return useQuery<DataConnector[], Error>({
    queryKey: ['data_connectors'],
    queryFn: async () => {
      const accessToken = await getAccessTokenSilently();
      return getConnectors({ accessToken });
    },
    staleTime: Infinity,
    refetchOnWindowFocus: false
  });
};

export const useConnector = ({ connectorId }: { connectorId: string }) => {
  const { getAccessTokenSilently } = useAuth0();

  return useQuery<DataConnector, Error>({
    queryKey: ['data_connectors/connector', connectorId],
    queryFn: async () => {
      const accessToken = await getAccessTokenSilently();
      return getConnector({ accessToken, connectorId });
    },
    staleTime: Infinity,
    refetchOnWindowFocus: false
  });
};

export const useCreateConnection = ({
  connectorId,
  publicKey
}: {
  connectorId: string;
  publicKey: PublicKey;
}) => {
  const queryClient = useQueryClient();
  const { getAccessTokenSilently } = useAuth0();

  return useMutation<DataConnection, Error, { connectionInfo: any; connectionName: string }>({
    mutationFn: async ({
      connectionInfo,
      connectionName
    }: {
      connectionInfo: any;
      connectionName: string;
    }) => {
      const accessToken = await getAccessTokenSilently();
      return createConnection({
        accessToken,
        connectorId,
        connectionInfo,
        connectionName,
        publicKey
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: getUserConnectionsQueryKey() });
    }
  });
};

export const usePublicKey = () => {
  const { getAccessTokenSilently } = useAuth0();

  return useQuery<PublicKey, Error>({
    queryKey: ['data_connectors/public_key'],
    queryFn: async (): Promise<PublicKey> => {
      const accessToken = await getAccessTokenSilently();
      return getPublicKey({ accessToken });
    },
    staleTime: Infinity,
    refetchOnWindowFocus: false
  });
};

export const useEnableConnectionStream = () => {
  const { getAccessTokenSilently } = useAuth0();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      connectionId,
      conversationId = 'background_tasks',
      onMessage,
      waitForSchema = true,
      attachToConversation = conversationId !== 'background_tasks',
      signal
    }: {
      connectionId: string;
      conversationId?: string;
      onMessage?: Parameters<typeof streamingEnableConnection>[0]['onMessage'];
      waitForSchema?: boolean;
      attachToConversation?: boolean;
      signal?: AbortSignal;
    }) => {
      const accessToken = await getAccessTokenSilently();
      await streamingEnableConnection({
        accessToken,
        connectionId,
        conversationId,
        onMessage,
        waitForSchema,
        attachToConversation,
        signal
      });
    },
    onSettled: (_data, _error, variables) => {
      const conversationId = variables.conversationId ?? 'background_tasks';
      const attachedToConversation =
        variables.attachToConversation ?? conversationId !== 'background_tasks';
      if (attachedToConversation) {
        void queryClient.invalidateQueries({
          queryKey: getConversationConnectionsQueryKey(conversationId)
        });
      }
    }
  });
};

export const useDisableConnectionDynamic = () => {
  const { getAccessTokenSilently } = useAuth0();
  const queryClient = useQueryClient();

  return useMutation<boolean, Error, { connectionId: string; conversationId: string }>({
    mutationFn: async ({ connectionId, conversationId }) => {
      const accessToken = await getAccessTokenSilently();
      return disableConnection({ accessToken, connectionId, conversationId });
    },
    onSuccess: (_data, { connectionId, conversationId }) => {
      queryClient.setQueryData<DataConnection[]>(
        getConversationConnectionsQueryKey(conversationId),
        (connections) => connections?.filter((connection) => connection.id !== connectionId)
      );
    }
  });
};

export const useDeleteConnection = () => {
  const { getAccessTokenSilently } = useAuth0();
  const queryClient = useQueryClient();
  return useMutation<void, Error, { connectionId: string }>({
    mutationFn: async ({ connectionId }) => {
      const accessToken = await getAccessTokenSilently();
      await deleteConnection({ accessToken, connectionId });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: getUserConnectionsQueryKey() });
      queryClient.invalidateQueries({ queryKey: ['google-ads-connected-accounts'] });
    }
  });
};

export const useUpdateConnection = () => {
  const { getAccessTokenSilently } = useAuth0();
  const queryClient = useQueryClient();

  return useMutation<
    DataConnection,
    Error,
    {
      connectionId: string;
      connectionName: string;
      connectionInfo: any;
      publicKey: PublicKey;
      abortSignal?: AbortSignal;
    }
  >({
    mutationFn: async ({
      connectionId,
      connectionName,
      connectionInfo,
      publicKey,
      abortSignal
    }) => {
      const accessToken = await getAccessTokenSilently();
      const result = await updateConnection({
        accessToken,
        connectionId,
        connectionName,
        connectionInfo,
        publicKey,
        abortSignal
      });
      queryClient.invalidateQueries({ queryKey: getUserConnectionsQueryKey() });
      return result;
    }
  });
};

export const useUpdateConnectionCustomInstructions = () => {
  const { getAccessTokenSilently } = useAuth0();
  const queryClient = useQueryClient();
  return useMutation<void, Error, { connectionId: string; customInstructions: string }>({
    mutationFn: async ({ connectionId, customInstructions }) => {
      const accessToken = await getAccessTokenSilently();
      await updateConnectionCustomInstructions({ accessToken, connectionId, customInstructions });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: getUserConnectionsQueryKey() });
    }
  });
};

export const useUpdateConnectionName = () => {
  const { getAccessTokenSilently } = useAuth0();
  const queryClient = useQueryClient();
  return useMutation<DataConnection, Error, { connectionId: string; connectionName: string }>({
    mutationFn: async ({ connectionId, connectionName }) => {
      const accessToken = await getAccessTokenSilently();
      const result = await updateConnectionName({ accessToken, connectionId, connectionName });
      queryClient.invalidateQueries({ queryKey: getUserConnectionsQueryKey() });
      return result;
    }
  });
};

export const useGetStaticIp = () => {
  return useQuery<StaticIpResponse, Error>({
    queryKey: ['data_connectors/static_ip'],
    queryFn: getStaticIp,
    staleTime: 0
  });
};

export const getLearningQueryKey = (connectionId: string) => [
  'data_connectors/learning',
  connectionId
];

export const useFreezeLearning = () => {
  const { getAccessTokenSilently } = useAuth0();
  const queryClient = useQueryClient();
  return useMutation<
    void,
    Error,
    { connectionId: string; setFreeze: boolean; teamId?: string }
  >({
    mutationFn: async ({ connectionId, setFreeze }) => {
      const accessToken = await getAccessTokenSilently();
      await freezeLearning({ accessToken, connectionId, setFreeze });
    },
    onSuccess: (_, { connectionId, teamId }) => {
      void queryClient.invalidateQueries({ queryKey: getLearningQueryKey(connectionId) });
      if (teamId) {
        void queryClient.invalidateQueries({ queryKey: ['team_data_connections', teamId] });
      } else {
        void queryClient.invalidateQueries({ queryKey: getUserConnectionsQueryKey() });
      }
    }
  });
};

export const useUpdateLearning = () => {
  const { getAccessTokenSilently } = useAuth0();
  const queryClient = useQueryClient();
  return useMutation<
    void,
    Error,
    { connectionId: string; learningData: Record<string, string>; teamId?: string }
  >({
    mutationFn: async ({ connectionId, learningData }) => {
      const accessToken = await getAccessTokenSilently();
      await updateLearning({ accessToken, connectionId, learningData });
    },
    onSuccess: (_, { connectionId, teamId }) => {
      void queryClient.invalidateQueries({ queryKey: getLearningQueryKey(connectionId) });
      if (teamId) {
        void queryClient.invalidateQueries({ queryKey: ['team_data_connections', teamId] });
      } else {
        void queryClient.invalidateQueries({ queryKey: getUserConnectionsQueryKey() });
      }
    }
  });
};

export const useGetLearning = ({
  connectionId,
  enabled = true
}: {
  connectionId: string;
  enabled?: boolean;
}) => {
  const { getAccessTokenSilently } = useAuth0();
  return useQuery<Record<string, any>, Error>({
    queryKey: getLearningQueryKey(connectionId),
    queryFn: async () => {
      const accessToken = await getAccessTokenSilently();
      return await getLearning({ accessToken, connectionId });
    },
    enabled: !!connectionId && enabled,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    retry: false
  });
};

export const queryConnectionDataQueryKey = (
  connectionId: string,
  params: ListConnectionDataParams
) => ['data_connectors/connection_data', connectionId, params];

export const queryConnectionDataOptions = ({
  connectionId,
  conversationId,
  accessToken,
  params
}: ListConnectionDataOptions) =>
  queryOptions({
    queryKey: queryConnectionDataQueryKey(connectionId, params),
    queryFn: async () => {
      return await listConnectionData({
        accessToken,
        connectionId,
        conversationId,
        params
      });
    }
  });

export const useQueryConnectionData = (
  props: Omit<ListConnectionDataOptions, 'accessToken'>,
  enabled: boolean = false
) => {
  const { getAccessTokenSilently } = useAuth0();

  return useQuery<ConnectionData[], { error: string }>({
    queryKey: queryConnectionDataQueryKey(props.connectionId, props.params),
    queryFn: async () => {
      const accessToken = await getAccessTokenSilently();
      return await listConnectionData({ accessToken, ...props });
    },
    retry: false,
    enabled
  });
};

export const getAutoapprovalModeQueryKey = (connectionId: string) => [
  'data_connectors/autoapproval',
  connectionId
];

export const useGetAutoapprovalMode = ({
  connectionId,
  enabled = true
}: {
  connectionId: string;
  enabled?: boolean;
}) => {
  const { getAccessTokenSilently } = useAuth0();
  return useQuery<{ autoapproval_enabled: boolean }, Error>({
    queryKey: getAutoapprovalModeQueryKey(connectionId),
    queryFn: async () => {
      const accessToken = await getAccessTokenSilently();
      return await getAutoapprovalMode({ accessToken, connectionId });
    },
    enabled: !!connectionId && enabled,
    staleTime: Infinity,
    refetchOnWindowFocus: false
  });
};

export const useSetAutoapprovalMode = () => {
  const { getAccessTokenSilently } = useAuth0();
  const queryClient = useQueryClient();
  return useMutation<
    { success: boolean; autoapproval_enabled: boolean },
    Error,
    { connectionId: string; autoapprovalEnabled: boolean },
    { previousValue: { autoapproval_enabled: boolean } | undefined }
  >({
    mutationFn: async ({ connectionId, autoapprovalEnabled }) => {
      const accessToken = await getAccessTokenSilently();
      return await setAutoapprovalMode({ accessToken, connectionId, autoapprovalEnabled });
    },
    onMutate: async ({ connectionId, autoapprovalEnabled }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: getAutoapprovalModeQueryKey(connectionId) });

      // Snapshot the previous value
      const previousValue = queryClient.getQueryData<{ autoapproval_enabled: boolean }>(
        getAutoapprovalModeQueryKey(connectionId)
      );

      // Optimistically update to the new value
      queryClient.setQueryData(getAutoapprovalModeQueryKey(connectionId), {
        autoapproval_enabled: autoapprovalEnabled
      });

      // Return context with the previous value
      return { previousValue };
    },
    onError: (_error, { connectionId }, context) => {
      // Rollback to the previous value on error
      if (context?.previousValue) {
        queryClient.setQueryData(getAutoapprovalModeQueryKey(connectionId), context.previousValue);
      }
    },
    onSettled: (_data, _error, { connectionId }) => {
      // Refetch after error or success to ensure cache is in sync
      queryClient.invalidateQueries({ queryKey: getAutoapprovalModeQueryKey(connectionId) });
    }
  });
};
